import { Layout } from "@/Layout";
import { ErrorScreen } from "@/components/ErrorScreen";
import { FeatureDisabledScreen } from "@/components/FeatureDisabledScreen";
import { WorkflowSidebar } from "@/components/WorkflowSidebar";
import { CommandBar } from "@/components/playground/CommandBar/CommandBar";
import { useStudioStore } from "@/components/playground/store";
import { ThemeProvider } from "@/components/theme-provider";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useIsLgScreen } from "@/hooks";
import { isFeatureDisabledError } from "@/lib/api";
import { workflowsQueryOptions } from "@/lib/hooks/useWorkflows";
import { cn } from "@/lib/utils";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

export const Route = createFileRoute("/workflows")({
  component: WorkflowLayout,
  loader: async ({ context: { queryClient } }) => {
    // FIXME - The types are wonky here, `workflowsResponse` is `any`
    const workflowsResponse = await queryClient.ensureQueryData(
      workflowsQueryOptions(),
    );

    return {
      workflows: workflowsResponse.data,
    };
  },
  errorComponent: ({ error }) => {
    const { partitionKey } = useStudioStore("partitionKey");
    if (isFeatureDisabledError(error)) {
      return (
        <FeatureDisabledScreen
          error={error}
          title="Workflows are Disabled"
          message="To use workflows, configure Fiberplane with an API key."
        />
      );
    }
    if (!partitionKey) {
      return (
        <FeatureDisabledScreen
          error={error}
          title="Missing workflows configuration"
          message="To use workflows, you need to add a partition key in the settings."
        />
      );
    }
    return (
      <ErrorScreen
        error={error}
        title="Error loading Workflows"
        message="An unknown error occurred, which is our least favourite kind of error."
      />
    );
  },
});

function getMainSectionWidth() {
  return window.innerWidth - 85;
}

function WorkflowLayout() {
  const [sidePanel] = useState<"open" | "closed">("open");
  const isLgScreen = useIsLgScreen();
  const width = getMainSectionWidth();
  const [commandBarOpen, setCommandBarOpen] = useState(false);

  useHotkeys("meta+k", () => {
    setCommandBarOpen(true);
  });
  // Panel constraints for responsive layout
  const minSize = (320 / width) * 100;

  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <Layout>
        <div className={cn("h-[calc(100vh-70px)]", "grid", "gap-2", "p-2")}>
          <CommandBar open={commandBarOpen} setOpen={setCommandBarOpen} />
          <ResizablePanelGroup direction="horizontal" className="w-full">
            {isLgScreen && sidePanel === "open" && (
              <>
                <ResizablePanel
                  id="sidebar"
                  order={0}
                  minSize={minSize}
                  // maxSize={maxSize}
                  defaultSize={(320 / width) * 100}
                >
                  <div
                    className={cn(
                      "px-4 overflow-hidden border rounded-md",
                      "h-full",
                      "flex",
                      "flex-col",
                      "pt-4",
                    )}
                  >
                    <WorkflowSidebar />
                  </div>
                </ResizablePanel>
                <ResizableHandle
                  hitAreaMargins={{ coarse: 20, fine: 10 }}
                  className="w-0 mr-2"
                />
              </>
            )}
            <ResizablePanel id="main" order={1}>
              <div className="flex flex-col h-full min-h-0">
                <Outlet />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </Layout>
    </ThemeProvider>
  );
}
